import React, { useCallback, useEffect, useState } from 'react';
import PropTypes, { checkPropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ControlPanel, ControlPanelItem, CarouselStepper } from '@sumtotal/react-foundation';
import styled from 'styled-components';
import useOverdueCountContext from '_common/hooks/useOverdueCountContext';
import useCustomNavigation from '../../hooks/useCustomNavigation';
import { postMessage } from '../../../modules/core/services/coreIFrameHelper';
import { GetProcessedNodeUrl } from '../../../utils/urlHelper';
import useHeaderContext from '../../hooks/useHeaderContext';
import useUserContext from '../../hooks/useUserContext';
import getNotificationCount from '../../topnav/services/timelineService';

import getLogger from '../../../utils/logger';

const StyledControlPanelItem = styled(ControlPanelItem)`
  button {
    min-height: ${(props) => (props.configurationTypeForMinHeight === 1 ? '3rem' : '3.75rem')};
    margin-top: 0.13rem;
    background-color: ${(props) => {
      if (
        props.highlightControlPanelItem &&
        (props.navigatioUrl === '' ||
          (props.navigatioUrl === '/' &&
            window.location.pathname === `/rcore${props.homePageUrl.split('?')[0]}`) ||
          window.location.pathname === `/rcore${props.navigatioUrl.split('?')[0]}`)
      )
        return props.theme.variables.topbarNavActiveStateBackground;
      return 'inherit';
    }};
    outline: ${(props) => {
      if (
        props.highlightControlPanelItem &&
        (props.navigatioUrl === '' ||
          (props.navigatioUrl === '/' &&
            window.location.pathname === `/rcore${props.homePageUrl.split('?')[0]}`) ||
          window.location.pathname === `/rcore${props.navigatioUrl.split('?')[0]}`)
      )
        return `${props.theme.variables.topbarNavTabOutlineColor} solid 0.0625rem`;
      return 'inherit';
    }};
    color: ${(props) => {
      if (
        props.highlightControlPanelItem &&
        (props.navigatioUrl === '' ||
          (props.navigatioUrl === '/' &&
            window.location.pathname === `/rcore${props.homePageUrl.split('?')[0]}`) ||
          window.location.pathname === `/rcore${props.navigatioUrl.split('?')[0]}`)
      )
        return props.theme.variables.topbarNavActiveStateTextColor;
      return props.theme.variables.leftNavParentColor;
    }};
    &:focus {
      background-color: ${(props) => {
        if (
          props.highlightControlPanelItem &&
          (props.navigatioUrl === '' ||
            (props.navigatioUrl === '/' &&
              window.location.pathname === `/rcore${props.homePageUrl.split('?')[0]}`) ||
            window.location.pathname === `/rcore${props.navigatioUrl.split('?')[0]}`)
        )
          return props.theme.variables.topbarNavActiveStateBackground;
        return props.theme.variables.topbarNavHoverStateBackground;
      }};
    }
    &.Mui-disabled {
      color: ${(props) => props.theme.variables.headerBarColor};
      opacity: 0.5;
    }
  }
  & .MuiBadge-colorPrimary {
    color: #fff;
  }
`;

const StyledControlPanel = styled(ControlPanel)`
  height: calc(100% - 5.563rem);
  display: flex;
  flex-direction: column;
  .MuiButton-outlined.Mui-disabled {
    border: none;
  }
`;

const CustomMobileStepperContainer = styled.div`
  position: fixed;
  bottom: 1.875rem;
  left: 0;
  border-top: 0.063rem solid ${(props) => props.theme.variables.btnPrimaryColor};
  .MuiMobileStepper-root {
    box-shadow: none;
  }
`;
/**
 *  Component for Control Panel Container.
 *
 */
function ControlPanelContainer(props) {
  const {
    controlPanelItems = defaultProps.controlPanelItems,
    highlightControlPanelItem = defaultProps.highlightControlPanelItem,
    setHighlightCPItem = defaultProps.setHighlightCPItem,
    panelExpanded = defaultProps.panelExpanded,
    panelWidth = defaultProps.panelWidth,
    controlpanelId = defaultProps.controlpanelId,
    closeCallback = defaultProps.closeCallback,
    enableControlPanelItemTooltip = defaultProps.enableControlPanelItemTooltip,
    focusable = defaultProps.focusable,
    expandCallback = defaultProps.expandCallback
  } = props;
  checkPropTypes(ControlPanelContainer.propTypes, props, 'prop', ControlPanelContainer.name);
  const { t } = useTranslation(['TRANSLATIONS_RCORE_TOPNAV']);
  const navigate = useCustomNavigation();
  const [ControlPanelItemList, setControlPanelItemList] = useState([]);
  const [PageControlPanelItemList, setPageControlPanelItemList] = useState([]);
  const nodes = controlPanelItems.Nodes;
  const footerNode = controlPanelItems.Footer;
  const headerData = useHeaderContext();
  const { headerContext, setHeaderContext } = headerData;
  const [timelineCount, setTimelineCount] = useState();
  const { user } = useUserContext();
  const disableControlPanel = user.isEmulatedUser ? false : user.mustAnswerSecurityQuestions;
  const [calcHeight, updateCalcHeight] = useState(0);
  const [resetPage, setResetPage] = useState(false);
  const [maxCountPerPage, setMaxCountPerPage] = useState(0);
  const [pagesLength, setPagesLength] = useState(1);

  const overdueCountData = useOverdueCountContext();
  const { overdueCountContext } = overdueCountData;
  const logger = getLogger();
  const itemHeight = controlPanelItems.ConfigurationType === 1 ? 50 : 65;
  const minHeightValue = controlPanelItems.ConfigurationType === 1 ? 168 : 202;
  const heightToOffset = controlPanelItems.ConfigurationType === 1 ? 178 : 190;

  const updateSize = () => {
    const clientheight = document.getElementById('modal-sidebar-controlpanel-items')?.clientHeight;
    updateCalcHeight(clientheight);
    if (clientheight > minHeightValue + 10) {
      if (clientheight - (ControlPanelItemList.length + 1) * itemHeight < 80) {
        let countPerPage = Math.floor((clientheight - heightToOffset) / itemHeight);
        countPerPage = countPerPage > 0 ? countPerPage : 1;
        const numOfPages = Math.ceil(ControlPanelItemList.length / countPerPage);
        if (countPerPage !== maxCountPerPage) {
          setPagesLength(numOfPages);
          setMaxCountPerPage(countPerPage);
          setResetPage(true);
          setPageControlPanelItemList(ControlPanelItemList.slice(0, countPerPage));
        }
      } else {
        setPagesLength(1);
        setResetPage(true);
        setMaxCountPerPage(ControlPanelItemList.length);
        setPageControlPanelItemList(ControlPanelItemList);
      }
    } else {
      setPagesLength(0);
      setResetPage(true);
      setMaxCountPerPage(0);
      setPageControlPanelItemList([]);
    }
  };

  const timeLineBadgeAriaLable = useCallback((Count) => {
    if (Count > 1) {
      return t('RCORE_TOPNAV_TIMELINE_ITEMS', {
        Count
      });
    }
    return t('RCORE_TOPNAV_TIMELINE_ITEM', {
      Count
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Data required for Control Panel
    const ControlPanelData = [];
    if (nodes != null) {
      nodes.forEach((item) => {
        const node = item;
        if (node.nodeKey === 'home_header') {
          node.url = '/';
        } else if (
          node.nodeKey === 'alert_header' &&
          (headerContext.refreshTimeline === undefined || headerContext.refreshTimeline)
        ) {
          const forceRefresh =
            headerContext.refreshTimeline === undefined ? false : headerContext.refreshTimeline;
          getNotificationCount(null, forceRefresh, true)
            .then((Count) => {
              if (Count > 0) {
                node.showBadgeIcon = true;
                node.ariaLabel = timeLineBadgeAriaLable(Count);
              } else {
                node.showBadgeIcon = false;
              }
              setTimelineCount(Count);
              postMessage(
                'REFRESH_TIMELINE_COUNT',
                { notificationCount: Count },
                false,
                null,
                false,
                true
              );
              if (headerContext.refreshTimeline) {
                setHeaderContext((state) => ({
                  ...state,
                  refreshTimeline: false
                }));
              }
              return Count;
            })
            .catch((err) => {
              logger.error(err);
              return 0;
            });
        } else if (node.nodeKey === 'my_activities') {
          const getMyTodosOverDueCount = async () => {
            try {
              if (overdueCountContext > 0) {
                node.showBadgeIcon = true;
                node.ariaLabel = timeLineBadgeAriaLable(overdueCountContext);
              } else {
                node.showBadgeIcon = false;
                node.ariaLabel = timeLineBadgeAriaLable(overdueCountContext);
              }
            } catch (error) {
              logger.error(error);
            }
          };
          getMyTodosOverDueCount();
        }
        ControlPanelData.push(node);
      });
    }
    setControlPanelItemList(ControlPanelData);
    setPageControlPanelItemList(ControlPanelData);
    if (footerNode != null) {
      footerNode.url = GetProcessedNodeUrl(footerNode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nodes,
    footerNode,
    headerContext.refreshTimeline,
    setHeaderContext,
    overdueCountContext,
    timeLineBadgeAriaLable
  ]);

  useEffect(() => {
    if (!panelExpanded) {
      updateSize();
      window.addEventListener('resize', updateSize);
    }
    return () => window.removeEventListener('resize', updateSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcHeight, ControlPanelItemList, panelExpanded]);

  function handleItemClick(event, nodeKey, action) {
    closeCallback();
    setHighlightCPItem(nodeKey);
    if (action === 'OPEN_LIBRARY') {
      if (window.location.pathname.indexOf('/c/') > 0) {
        postMessage(action, null, null, event.currentTarget.id);
        return;
      }
      const libNode = ControlPanelItemList.find((item) => item.nodeKey === 'Learner_MenuTaxonomy');
      navigate(libNode.url);
    } else if (action !== 'NAVIGATE') {
      postMessage(action, null, null, event.currentTarget.id);
    } else return null;
  }

  function handleItemMouseClick(event, nodeKey, action, url) {
    if (
      action === 'NAVIGATE' &&
      (event.keyCode === 13 || event.button === 1 || event.button === 0)
    ) {
      setHighlightCPItem(nodeKey);
      navigate(url);
      closeCallback();
    }
  }

  const getBadgeCount = ({ nodeKey }) => {
    if (nodeKey === 'alert_header') return timelineCount;
    if (nodeKey === 'my_activities') return overdueCountContext;
    return null;
  };

  const handleLeftArrowClick = (activePage) => {
    if (activePage >= 0) {
      setPageControlPanelItemList(
        ControlPanelItemList.slice(activePage * maxCountPerPage, (activePage + 1) * maxCountPerPage)
      );
      setResetPage(activePage === 0);
      setTimeout(() => {
        const contentElement = document.getElementById('modal-sidebar-controlpanel-items');
        if (contentElement !== undefined) {
          contentElement.firstChild?.childNodes[1]?.firstChild?.focus();
        }
      }, 500);
    }
  };

  const handleRightArrowClick = (activePage) => {
    if (activePage < pagesLength) {
      setPageControlPanelItemList(
        ControlPanelItemList.slice(activePage * maxCountPerPage, (activePage + 1) * maxCountPerPage)
      );
      setResetPage(activePage === 0);
      setTimeout(() => {
        const contentElement = document.getElementById('modal-sidebar-controlpanel-items');
        if (contentElement !== undefined) {
          contentElement.firstChild?.childNodes[1]?.firstChild?.focus();
        }
      }, 500);
    }
  };
  const handleExpandCollapsClick = () => {
    if (panelExpanded) {
      closeCallback();
    } else {
      expandCallback();
    }
  };
  return (
    <>
      <div id="modal-sidebar-backdrop" className="modal-backdrop sidebar-backdrop" />

      <StyledControlPanel
        id={controlpanelId}
        role="navigation"
        ariaLabel={t('RCORE_TOPNAV_CONTROLPANEL_REGION')}
        className="no-print"
        width={panelWidth}
        panelExpanded={panelExpanded}
      >
        <StyledControlPanelItem
          isDisabled={disableControlPanel}
          key={panelExpanded ? 'collapse_menu' : 'expand_menu'}
          iconType="IcomoonIcon"
          iconName={panelExpanded ? 'Menu-Collapsed' : 'Menu-Expanded'}
          iconSize={1.75}
          btnSize="large"
          name={
            panelExpanded
              ? t('RCORE_CONTROL_PANEL_COLLAPSE_MENU')
              : t('RCORE_CONTROL_PANEL_EXPAND_MENU')
          }
          role="button"
          ariaLabel={
            panelExpanded
              ? t('RCORE_CONTROL_PANEL_COLLAPSE_BUTTON')
              : t('RCORE_CONTROL_PANEL_EXPAND_BUTTON')
          }
          nodeKey={panelExpanded ? 'collapse_menu' : 'expand_menu'}
          panelExpanded={panelExpanded}
          enableTooltip
          onItemClick={handleExpandCollapsClick}
          configurationTypeForMinHeight={controlPanelItems.ConfigurationType}
          configurationType={panelExpanded ? 2 : controlPanelItems.ConfigurationType}
        />
        {PageControlPanelItemList &&
          PageControlPanelItemList.map((item) => (
            <StyledControlPanelItem
              isDisabled={disableControlPanel}
              key={item.nodeKey}
              iconType={item.iconType}
              iconName={item.iconName}
              iconSize={1.75}
              btnSize="large"
              name={item.name}
              navigatioUrl={item.actionType === 'NAVIGATE' ? item.url : ''}
              role={item.actionType === 'NAVIGATE' ? 'link' : 'button'}
              actionType={item.actionType}
              onItemClick={item.actionType !== 'NAVIGATE' ? handleItemClick : undefined}
              onKeyDown={item.actionType === 'NAVIGATE' ? handleItemMouseClick : undefined}
              onMouseDown={item.actionType === 'NAVIGATE' ? handleItemMouseClick : undefined}
              showBadgeIcon={item.showBadgeIcon}
              notificationCount={getBadgeCount(item)}
              ariaLabel={
                getBadgeCount(item) > 0
                  ? `${item.name} ${getBadgeCount(item)} ${t(
                      'RCORE_TOPNAV_CONTROLPANEL_REGION_TXT'
                    )}`
                  : item.name
              }
              nodeKey={item.nodeKey}
              highlightControlPanelItem={item.nodeKey === highlightControlPanelItem}
              homePageUrl={user.homePageUrl}
              panelExpanded={panelExpanded}
              enableTooltip={enableControlPanelItemTooltip}
              tabIndex={focusable ? 0 : -1}
              ariaCurrent={
                item.nodeKey === highlightControlPanelItem
                  ? t('RCORE_TOPNAV_CONTROLPANEL_PAGE_TXT')
                  : 'false'
              }
              configurationTypeForMinHeight={controlPanelItems.ConfigurationType}
              configurationType={panelExpanded ? 2 : controlPanelItems.ConfigurationType}
            />
          ))}
      </StyledControlPanel>
      {pagesLength > 1 && (
        <CustomMobileStepperContainer>
          <CarouselStepper
            steps={pagesLength}
            nextButtonCallback={handleRightArrowClick}
            backButtonCallback={handleLeftArrowClick}
            buttonsPosition="bottom"
            resetActiveStep={resetPage}
          />
        </CustomMobileStepperContainer>
      )}
    </>
  );
}
// PropType check
ControlPanelContainer.propTypes = {
  /**
   * Control panel items
   */
  controlPanelItems: PropTypes.shape({
    Nodes: PropTypes.arrayOf(PropTypes.shape({})),
    Footer: PropTypes.shape({ url: PropTypes.string }),
    ConfigurationType: PropTypes.oneOf([0, 1, 2])
  }),
  /**
   * Highlight Control panel item
   */
  highlightControlPanelItem: PropTypes.string,
  /**
   * function highlight Control panel item
   */
  setHighlightCPItem: PropTypes.func,
  /**
   * State of the control panel.
   */
  panelExpanded: PropTypes.bool,
  /**
   * Width of the control panel
   */
  panelWidth: PropTypes.number,
  /**
   * Id of panel
   */
  controlpanelId: PropTypes.string,
  closeCallback: PropTypes.func,
  /**
   * enable control panel item tooltip
   */
  enableControlPanelItemTooltip: PropTypes.bool,
  /**
   * control panel item can be focused
   */
  focusable: PropTypes.bool,
  /**
   * Callback function when control panel is expanded
   */
  expandCallback: PropTypes.func
};

const defaultProps = {
  controlPanelItems: {},
  highlightControlPanelItem: '',
  setHighlightCPItem: undefined,
  mouseOverCallback: () => {},
  panelExpanded: false,
  panelWidth: undefined,
  controlpanelId: undefined,
  closeCallback: undefined,
  enableControlPanelItemTooltip: true,
  focusable: true,
  expandCallback: () => {}
};

export default ControlPanelContainer;
